import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LuChevronDown, LuLock, LuPanelLeftClose, LuPanelLeftOpen, LuSearch } from 'react-icons/lu'
import { useMenu } from '../../auth/PermissionContext'
import { MenuIcon } from '../../auth/iconMap'
import { resolveMenuLabel } from '../../auth/resolveMenuLabel'
import type { MeMenuItem } from '../../api/services/me.service'
import { accentFor, fonts, mineral } from '../../theme'
import { useAuth } from '../../contexts/AuthContext'
import KunturMark from '../brand/KunturMark'
import { canOpen, findActive, flattenLeaves } from './nav'

export const RAIL_W = 256

/** Riel claro (arena/champaña): tinta cálida en vez de blanco sobre ocre oscuro. */
const R = {
  bg: 'linear-gradient(180deg, #f7ead0 0%, #faf1df 45%, #fcf7ee 100%)',
  ink: mineral.ink,
  text: mineral.ink2,
  muted: mineral.ink3,
  faint: '#a99a86',
  hover: 'rgba(140, 100, 40, 0.07)',
  sunk: 'rgba(140, 100, 40, 0.10)',
  line: 'rgba(140, 100, 40, 0.16)',
}
export const RAIL_W_COLLAPSED = 76

const spring = { type: 'spring', stiffness: 520, damping: 40, mass: 0.7 } as const

interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed?: () => void
  onNavigate: (item: MeMenuItem) => void
  onOpenSearch: () => void
  /** En el drawer móvil el riel siempre va expandido y sin botón de colapsar. */
  variant?: 'rail' | 'drawer'
}

function groupsOf(section: MeMenuItem): Array<{ group: MeMenuItem | null; leaves: MeMenuItem[] }> {
  const out: Array<{ group: MeMenuItem | null; leaves: MeMenuItem[] }> = []
  const loose: MeMenuItem[] = []
  for (const c of section.children ?? []) {
    if (c.isGroup) out.push({ group: c, leaves: (c.children ?? []).filter(l => !l.isGroup) })
    else loose.push(c)
  }
  if (loose.length) out.unshift({ group: null, leaves: loose })
  return out
}

function Leaf({ item, active, accent, onNavigate }: { item: MeMenuItem; active: boolean; accent: string; onNavigate: (i: MeMenuItem) => void }) {
  const { t } = useTranslation()
  const blocked = !item.allowed
  return (
    <Box
      as="button"
      {...{ type: 'button' }}
      position="relative"
      display="flex"
      alignItems="center"
      gap={2}
      w="100%"
      textAlign="left"
      pl={3}
      pr={2}
      py="7px"
      borderRadius="9px"
      fontSize="13.5px"
      color={active ? R.ink : blocked ? R.faint : R.text}
      fontWeight={active ? 600 : 400}
      cursor={blocked || item.isComingSoon ? 'not-allowed' : 'pointer'}
      _hover={blocked ? {} : { color: R.ink, bg: active ? undefined : R.hover }}
      transition="color .15s, background .15s"
      title={blocked ? 'Permisos insuficientes' : undefined}
      onClick={() => canOpen(item) && onNavigate(item)}
    >
      {active && (
        <motion.span
          layoutId="rail-active-leaf"
          transition={spring}
          style={{ position: 'absolute', inset: 0, borderRadius: 9, background: `${accent}2e` }}
        />
      )}
      <Text as="span" position="relative" flex={1} lineClamp={1}>{resolveMenuLabel(item.label, t)}</Text>
      {blocked && <LuLock size={11} style={{ position: 'relative', opacity: 0.7 }} />}
      {item.isComingSoon && !blocked && (
        <Text as="span" position="relative" fontSize="9px" fontFamily={fonts.mono} letterSpacing="0.06em" px={1.5} py="1px" borderRadius="4px"
          bg={R.sunk} color={R.muted}>PRONTO</Text>
      )}
    </Box>
  )
}

function SectionBody({ section, activeRoute, onNavigate }: { section: MeMenuItem; activeRoute: string | null; onNavigate: (i: MeMenuItem) => void }) {
  const { t } = useTranslation()
  const acc = accentFor(section.code)
  return (
    <Box pt={1} pb={2.5}>
      {groupsOf(section).map(({ group, leaves }, gi) => (
        <Box key={group?.id ?? `loose-${gi}`} mt={gi ? 2 : 0}>
          {group && (
            <Text px={3} pt={1} pb={1} fontFamily={fonts.mono} fontSize="9.5px" letterSpacing="0.14em" textTransform="uppercase" color={R.faint}>
              {resolveMenuLabel(group.label, t)}
            </Text>
          )}
          {leaves.map(l => (
            <Leaf key={l.id} item={l} active={!!l.route && l.route === activeRoute} accent={acc.color} onNavigate={onNavigate} />
          ))}
        </Box>
      ))}
    </Box>
  )
}

export default function Sidebar({ collapsed, onToggleCollapsed, onNavigate, onOpenSearch, variant = 'rail' }: SidebarProps) {
  const menu = useMenu()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { user } = useAuth()
  const leaves = useMemo(() => flattenLeaves(menu), [menu])
  const active = findActive(leaves, pathname)
  const activeSectionCode = active?.section.code ?? null
  const [open, setOpen] = useState<string | null>(activeSectionCode)
  const [flyout, setFlyout] = useState<{ code: string; top: number } | null>(null)
  const flyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Al navegar a otra sección (p. ej. desde la paleta), el acordeón la sigue.
  const prevActive = useRef(activeSectionCode)
  useEffect(() => {
    if (activeSectionCode && activeSectionCode !== prevActive.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sincroniza el acordeón con la ruta
      setOpen(activeSectionCode)
    }
    prevActive.current = activeSectionCode
  }, [activeSectionCode])

  const isCollapsed = variant === 'rail' && collapsed
  const showFlyout = (code: string, el: HTMLElement) => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current)
    setFlyout({ code, top: el.getBoundingClientRect().top })
  }
  const hideFlyout = () => { flyoutTimer.current = setTimeout(() => setFlyout(null), 140) }
  const keepFlyout = () => { if (flyoutTimer.current) clearTimeout(flyoutTimer.current) }
  const flyoutSection = flyout ? menu.find(s => s.code === flyout.code) : null

  return (
    <Flex
      direction="column"
      h="100%"
      w="100%"
      position="relative"
      overflow="hidden"
      color={R.ink}
      borderRight={`1px solid ${R.line}`}
      style={{ background: R.bg }}
    >
      {/* Marca */}
      <Flex position="relative" align="center" gap={3} px={isCollapsed ? 0 : 5} justify={isCollapsed ? 'center' : 'flex-start'} pt={5} pb={4} minH="84px">
        <Box color={mineral.ocreDeep} flexShrink={0}>
          <KunturMark width={isCollapsed ? 42 : 50} />
        </Box>
        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }} transition={{ duration: 0.18 }} style={{ minWidth: 0 }}>
              <Text fontFamily={fonts.display} fontWeight={800} fontSize="24px" lineHeight="1" letterSpacing="-0.03em">Kuntur</Text>
              <Text fontFamily={fonts.mono} fontSize="10px" letterSpacing="0.12em" color={R.muted} mt={1} lineClamp={1}>
                {(user?.tenantName ?? user?.tenantCode ?? 'TRAZABILIDAD').toUpperCase()}
              </Text>
            </motion.div>
          )}
        </AnimatePresence>
      </Flex>

      {/* Buscar */}
      <Box position="relative" px={isCollapsed ? 3 : 4} pb={3}>
        <Flex
          as="button"
          {...{ type: 'button' }}
          w="100%"
          align="center"
          justify={isCollapsed ? 'center' : 'flex-start'}
          gap={2.5}
          h="40px"
          px={isCollapsed ? 0 : 3}
          borderRadius="11px"
          bg="rgba(255,255,255,0.75)"
          border={`1px solid ${R.line}`}
          color={R.muted}
          fontSize="13px"
          cursor="pointer"
          transition="all .15s"
          _hover={{ bg: '#fff', color: R.ink, borderColor: 'rgba(140, 100, 40, 0.28)' }}
          onClick={onOpenSearch}
          title="Buscar o ir a… (Ctrl K)"
        >
          <LuSearch size={16} />
          {!isCollapsed && (
            <>
              <Text as="span" flex={1} textAlign="left">Buscar o ir a…</Text>
              <Text as="kbd" fontFamily={fonts.mono} fontSize="10.5px" px={1.5} py="1px" borderRadius="5px" bg={R.sunk} color={R.muted}>Ctrl K</Text>
            </>
          )}
        </Flex>
      </Box>

      {/* Secciones */}
      <Box position="relative" flex={1} overflowY="auto" overflowX="hidden" px={isCollapsed ? 2.5 : 3} pb={4}
        css={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(140, 100, 40, 0.2) transparent' }}>
        {menu.map(section => {
          const acc = accentFor(section.code)
          const isActive = section.code === activeSectionCode
          const hasChildren = (section.children?.length ?? 0) > 0
          const isOpen = open === section.code && hasChildren && !isCollapsed
          return (
            <Box key={section.id} mb={0.5}>
              <Flex
                as="button"
                {...{ type: 'button' }}
                w="100%"
                align="center"
                gap={3}
                px={isCollapsed ? 0 : 2}
                justify={isCollapsed ? 'center' : 'flex-start'}
                h="44px"
                borderRadius="12px"
                position="relative"
                cursor="pointer"
                bg={isActive && !isOpen ? 'rgba(255,255,255,0.7)' : 'transparent'}
                _hover={{ bg: isActive && !isOpen ? 'rgba(255,255,255,0.85)' : R.hover }}
                transition="background .15s"
                onClick={() => {
                  if (!hasChildren && section.route) onNavigate(section)
                  else if (!isCollapsed) setOpen(isOpen ? null : section.code)
                }}
                onMouseEnter={isCollapsed && hasChildren ? (e: React.MouseEvent<HTMLElement>) => showFlyout(section.code, e.currentTarget) : undefined}
                onMouseLeave={isCollapsed ? hideFlyout : undefined}
              >
                <Flex w="32px" h="32px" borderRadius="10px" align="center" justify="center" flexShrink={0}
                  bg={isActive ? acc.color : `${acc.color}1f`} color={isActive ? '#fff' : acc.color} transition="background .2s, color .2s">
                  <MenuIcon name={section.icon} size={17} />
                </Flex>
                {!isCollapsed && (
                  <>
                    <Text flex={1} textAlign="left" fontFamily={fonts.display} fontWeight={isActive ? 700 : 600} fontSize="14.5px" letterSpacing="-0.01em"
                      color={isActive ? R.ink : R.text} lineClamp={1}>
                      {resolveMenuLabel(section.label, t, { short: true })}
                    </Text>
                    {hasChildren && (
                      <Box color={R.faint} transition="transform .2s" transform={isOpen ? 'rotate(180deg)' : 'none'}>
                        <LuChevronDown size={14} />
                      </Box>
                    )}
                  </>
                )}
              </Flex>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }} style={{ overflow: 'hidden' }}>
                    <Box pl="40px">
                      <SectionBody section={section} activeRoute={active?.item.route ?? null} onNavigate={onNavigate} />
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          )
        })}
      </Box>

      {/* Pie */}
      {variant === 'rail' && onToggleCollapsed && (
        <Flex position="relative" align="center" justify={isCollapsed ? 'center' : 'space-between'} px={isCollapsed ? 0 : 5} py={3}
          borderTop={`1px solid ${R.line}`}>
          {!isCollapsed && (
            <Text fontFamily={fonts.mono} fontSize="10px" color={R.faint} letterSpacing="0.08em">v{__APP_VERSION__}</Text>
          )}
          <Box as="button" {...{ type: 'button' }} p={2} borderRadius="9px" color={R.muted} cursor="pointer"
            _hover={{ bg: R.hover, color: R.ink }} onClick={onToggleCollapsed}
            title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}>
            {isCollapsed ? <LuPanelLeftOpen size={17} /> : <LuPanelLeftClose size={17} />}
          </Box>
        </Flex>
      )}

      {/* Panel flotante en modo colapsado */}
      <AnimatePresence>
        {isCollapsed && flyoutSection && flyout && (
          <motion.div
            key={flyoutSection.code}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.16 }}
            onMouseEnter={keepFlyout}
            onMouseLeave={hideFlyout}
            style={{ position: 'fixed', left: RAIL_W_COLLAPSED + 6, top: Math.min(flyout.top - 8, window.innerHeight - 420), zIndex: 70, width: 248 }}
          >
            <Box borderRadius="14px" p={2} border={`1px solid ${R.line}`} boxShadow="0 24px 50px -16px rgba(90, 60, 20, 0.35)" color={R.ink}
              style={{ background: R.bg }} maxH="420px" overflowY="auto">
              <Flex align="center" gap={2} px={3} pt={2} pb={1}>
                <Box w="8px" h="8px" borderRadius="2px" bg={accentFor(flyoutSection.code).color} />
                <Text fontFamily={fonts.display} fontWeight={700} fontSize="14px">{resolveMenuLabel(flyoutSection.label, t)}</Text>
              </Flex>
              <SectionBody section={flyoutSection} activeRoute={active?.item.route ?? null} onNavigate={i => { setFlyout(null); onNavigate(i) }} />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  )
}
